import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, LayoutDashboard, Shield, Users, AlertTriangle, BarChart3, DollarSign, Activity, CheckCircle2, Edit, Brain
} from 'lucide-react'
import { programsService, Program } from '../services/programs'
import { projectsService, Project } from '../services/projects'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import { HeroSection } from '../components/HeroSection'
import { ProgramKPIIndicators } from '../components/programs/ProgramKPIIndicators'
import { ProgramProjectsStatus } from '../components/programs/ProgramProjectsStatus'
import RAIDLog from '../components/programs/RAIDLog'
import GovernanceReviews from '../components/programs/GovernanceReviews'
import QAMetricsComponent from '../components/programs/QAMetrics'
import CapacityAnalysisComponent from '../components/programs/CapacityAnalysis'
import BenefitsTracker from '../components/programs/BenefitsTracker'
import BudgetForecastChart from '../components/programs/BudgetForecastChart'
import { programKpiService } from '../services/programKPIs'
import { programBudgetService } from '../services/programBudgets'
import { programBudgetForecastService, BudgetForecast } from '../services/programBudgetForecast'
import { programRAIDService } from '../services/programRAID'
import { programGovernanceService } from '../services/programGovernance'
import { programResourcesService } from '../services/programResources'
import { aiInsightsService, AIInsight } from '../services/aiInsights'
import { InsightCard } from '../components/ui/InsightCard'
import { PredictionCard } from '../components/ui/PredictionCard'
import { BudgetCategories } from '../components/programs/BudgetCategories'
import { useAuth } from '../contexts/AuthContext'
import type { ApiErrorLike } from '../utils/apiError'


export default function ProgramWorkspace() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [program, setProgram] = useState<Program | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    if (id) {
      loadProgram()
    }
  }, [id])

  const loadProgram = async () => {
    setLoading(true)
    try {
      const programData = await programsService.get(parseInt(id!))
      setProgram(programData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching program:', error)
      toast.error(getErrorMessage(error) || 'Failed to load program')
      navigate('/program')
    } finally {
      setLoading(false)
    }
  }

  // IA #14 — consolidated from 8 → 5 tabs
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'delivery', label: 'Delivery', icon: Activity },
    { id: 'health', label: 'Health', icon: Shield },
    { id: 'people', label: 'People', icon: Users },
    { id: 'finance', label: 'Finance', icon: DollarSign },
  ]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading program...</p>
        </div>
      </div>
    )
  }

  if (!program) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-[#102a43]">Program not found</p>
          <button
            onClick={() => navigate('/program')}
            className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692]"
          >
            Back to Programs
          </button>
        </div>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { dot: 'bg-green-500', text: 'text-green-400', label: 'On Track' }
      case 'on_hold':
        return { dot: 'bg-orange-500', text: 'text-orange-400', label: 'At Risk' }
      case 'completed':
        return { dot: 'bg-blue-500', text: 'text-blue-400', label: 'Completed' }
      default:
        return { dot: 'bg-gray-400', text: 'text-gray-400', label: status }
    }
  }

  const statusColors = getStatusColor(program.status)

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Sidebar Navigation */}
      <div className="w-64 bg-[#486581] border-r border-[#5C748E] overflow-y-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/program')}
          className="w-full flex items-center gap-2 px-4 py-3 text-[#C5CDD6] hover:bg-[#3D4F61] border-b border-[#3D4F61]"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Programs</span>
        </button>
        
        <div className="p-4 border-b border-[#3D4F61]">
          <h2 className="font-medium mb-1 text-white text-sm truncate">{program.name}</h2>
          <p className="text-sm text-[#A3B1BF]">Program Management</p>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 ${statusColors.dot} rounded-full`}></div>
              <span className={`text-xs ${statusColors.text}`}>{statusColors.label}</span>
            </div>
            <button
              onClick={() => navigate(`/program/${id}/edit`)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-[#C5CDD6] hover:text-white hover:bg-[#3D4F61] rounded transition-colors"
            >
              <Edit className="w-3 h-3" />
              Edit
            </button>
          </div>
        </div>

        <div className="p-2">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#DAA520] text-white'
                    : 'text-[#C5CDD6] hover:bg-[#3D4F61] hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {activeTab === 'overview' && <ProgramOverview programId={parseInt(id!)} program={program} />}
        {activeTab === 'delivery' && <ProgramDeliveryTab programId={parseInt(id!)} />}
        {activeTab === 'health' && <ProgramHealthTab programId={parseInt(id!)} />}
        {activeTab === 'people' && <ProgramPeopleTab programId={parseInt(id!)} />}
        {activeTab === 'finance' && <ProgramFinanceTab programId={parseInt(id!)} />}
      </div>
    </div>
  )
}

// Program Overview Tab - Matches Figma design exactly
function ProgramOverview({ programId, program }: { programId: number; program: Program }) {
  const [kpis, setKpis] = useState<any>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [insights, setInsights] = useState<AIInsight[]>([])

  useEffect(() => {
    loadData()
  }, [programId])

  const loadData = async () => {
    try {
      const [kpisData, projectsData, insightData] = await Promise.all([
        programKpiService.list(programId).catch(() => []),
        projectsService.list({ program_id: programId }),
        // Sprint 11: was 'team' — that queried for team-scoped insights
        // keyed by the program id, which never matches. 'program' is the
        // right kind.
        aiInsightsService.list({ entity_type: 'program', entity_id: programId, limit: 3 }).catch(() => []),
      ])
      setInsights(insightData)

      const kpiMap: any = {}
      kpisData.forEach((kpi) => {
        kpiMap[kpi.kpi_type] = {
          current: Number(kpi.current_value) || 0,
          trend: Number(kpi.trend) || 0,
          status: kpi.status === 'At Risk' ? 'At Risk' : kpi.status === 'Excellent' ? 'Excellent' : 'On Track'
        }
      })

      const programKPIs = {
        schedulePerformance: kpiMap.schedule || { current: 68, trend: -12, status: 'At Risk' as const },
        budgetPerformance: kpiMap.budget || { current: 85, trend: 2, status: 'On Track' as const },
        scopeCompliance: kpiMap.scope || { current: 92, trend: 8, status: 'Excellent' as const },
        resourceAvailability: kpiMap.resource || { current: 78, trend: -5, status: 'At Risk' as const },
        stakeholderSatisfaction: kpiMap.stakeholder || { current: 88, trend: 3, status: 'On Track' as const }
      }

      setKpis(programKPIs)
      setProjects(projectsData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading overview data:', error)
    }
  }

  const handleProjectClick = (projectCode: string) => {
    const project = projects.find(p => p.id.toString() === projectCode)
    if (project) {
      window.location.href = `/project/${project.id}`
    }
  }

  // Transform projects to match Figma structure
  const programProjects = projects.map((project) => ({
    projectCode: project.id.toString(),
    projectName: project.name,
    completion: project.progress || 0,
    onTimeProbability: 85, // Calculate based on dates if available
    risks: 0, // Would need to fetch from project risks
    challenges: [] // Would need to fetch from project challenges
  }))

  const startDate = program.start_date ? new Date(program.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'
  const endDate = program.end_date ? new Date(program.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'
  const totalBudget = projects.reduce((sum, p) => sum + parseFloat(p.budget || '0'), 0)

  return (
    <div className="p-6 space-y-6">
      <HeroSection
        title={program.name}
        description={program.description || "No description yet."}
        variant="level2"
        metrics={[
          { label: 'Start Date', value: startDate },
          { label: 'Target Date', value: endDate },
          { label: 'Budget', value: formatCurrency(totalBudget) },
          { label: 'Team Size', value: `${projects.length} Projects` },
        ]}
        // Sprint 17 — dropped the "Export Report" actionButton that was
        // only `console.log('Export clicked')`. Real program export lives
        // at /api/v1/programs/{id}/reports/{report_id}/export but the
        // UX (pick report, pick format, trigger download) is deferred —
        // tracked in the dead-button backlog. Better to hide the control
        // than ship a button that does nothing.
      />
      <PredictionCard entityType="program" entityId={programId} />

      {/* Program Status Indicators - Enhanced Multi-Dimensional KPIs */}
      {kpis && (
        <div className="karyva-tile-level3">
          <ProgramKPIIndicators kpis={kpis} />
        </div>
      )}

      {/* AI Insights */}
      {insights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-[#DAA520]" />
            <h2 className="text-sm font-semibold text-[#2A3B4C]">AI Insights</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {insights.map(i => (
              <InsightCard key={i.id} insight={i} onFeedback={(id, score) => aiInsightsService.submitFeedback(id, score)} />
            ))}
          </div>
        </section>
      )}

      {/* Project Status Overview - Individual Project Drill-Down */}
      <div className="karyva-tile-level3">
        <ProgramProjectsStatus
          projects={programProjects}
          onProjectClick={handleProjectClick}
        />
      </div>
    </div>
  )
}

// ── IA #14: Delivery Tab — Projects + Milestones ──────────────────────────────
function ProgramDeliveryTab({ programId }: { programId: number }) {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadData() }, [programId])

  const loadData = async () => {
    setLoading(true)
    try {
      const data = await projectsService.list({ program_id: programId })
      setProjects(data)
    } catch { /* ignore */ } finally { setLoading(false) }
  }

  return (
    <div className="p-6 space-y-6">
      <HeroSection title="Delivery" description="Projects and milestone progress" variant="level3"
        metrics={[
          { label: 'Total Projects', value: projects.length.toString(), icon: BarChart3, iconColor: 'bg-blue-50' },
          { label: 'On Track', value: projects.filter(p => p.status === 'active').length.toString(), icon: CheckCircle2, iconColor: 'bg-green-50' },
        ]}
      />
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg text-neutral-950 mb-4">Projects</h3>
        {loading ? (
          <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500">No projects found</div>
        ) : (
          <div className="space-y-3">
            {projects.map(project => (
              <div key={project.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-4">
                <div>
                  <p className="text-sm font-medium text-neutral-950">{project.name}</p>
                  <p className="text-xs text-gray-500 capitalize mt-0.5">{project.status}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24">
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#486581] rounded-full" style={{ width: `${project.progress || 0}%` }} />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 text-right">{project.progress || 0}%</p>
                  </div>
                  <button
                    onClick={() => window.location.href = `/project/${project.id}`}
                    className="px-3 py-1.5 text-xs bg-[#486581] text-white rounded-lg hover:bg-[#3d5570] transition-colors min-h-[32px]"
                  >
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── IA #14: Health Tab — RAID + QA + Governance ───────────────────────────────
function ProgramHealthTab({ programId }: { programId: number }) {
  const [riskCount, setRiskCount] = useState(0)
  const [issueCount, setIssueCount] = useState(0)
  const [reviewCount, setReviewCount] = useState(0)
  const [compliance, setCompliance] = useState<any>(null)

  useEffect(() => {
    Promise.all([
      programRAIDService.list(programId).catch(() => []),
      programGovernanceService.listReviews(programId).catch(() => []),
      programGovernanceService.getCompliance(programId).catch(() => null),
    ]).then(([raid, gov, comp]) => {
      setRiskCount(raid.filter((i) => i.type === 'risk').length)
      setIssueCount(raid.filter((i) => i.type === 'issue').length)
      setReviewCount(gov.length)
      setCompliance(comp)
    })
  }, [programId])

  return (
    <div className="p-6 space-y-6">
      <HeroSection title="Health" description="RAID log, quality assurance, and governance" variant="level3"
        metrics={[
          { label: 'Open Risks', value: riskCount.toString(), icon: AlertTriangle, iconColor: 'bg-red-50' },
          { label: 'Open Issues', value: issueCount.toString(), icon: AlertTriangle, iconColor: 'bg-orange-50' },
          { label: 'Governance Reviews', value: reviewCount.toString(), icon: Shield, iconColor: 'bg-blue-50' },
          { label: 'Compliance', value: `${compliance?.compliance_score || 82}%`, icon: CheckCircle2, iconColor: 'bg-green-50' },
        ]}
      />
      <RAIDLog programId={programId} />
      <QAMetricsComponent programId={programId} />
      <GovernanceReviews programId={programId} />
    </div>
  )
}

// ── IA #14: People Tab — Resources + Capacity ────────────────────────────────
function ProgramPeopleTab({ programId }: { programId: number }) {
  const [allocCount, setAllocCount] = useState(0)
  const [utilizationRate, setUtilizationRate] = useState(0)

  useEffect(() => {
    Promise.all([
      programResourcesService.listAllocations(programId).catch(() => []),
      programResourcesService.getUtilization(programId).catch(() => null),
    ]).then(([allocs, util]) => {
      setAllocCount(allocs.length)
      setUtilizationRate(util?.utilization_rate || 0)
    })
  }, [programId])

  return (
    <div className="p-6 space-y-6">
      <HeroSection title="People" description="Resource allocation and team capacity" variant="level3"
        metrics={[
          { label: 'Allocated', value: allocCount.toString(), icon: Users, iconColor: 'bg-blue-50' },
          { label: 'Utilization', value: `${utilizationRate}%`, icon: Activity, iconColor: 'bg-green-50' },
        ]}
      />
      <CapacityAnalysisComponent programId={programId} />
    </div>
  )
}

// ── IA #14: Finance Tab — Budget + Forecast + Benefits ───────────────────────
function ProgramFinanceTab({ programId }: { programId: number }) {
  const { user } = useAuth()
  const canEditBudget = Boolean(user?.is_superuser || user?.roles?.some((r) => r.name === 'Organization Owner'))
  const [budgetSummary, setBudgetSummary] = useState<any>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [forecast, setForecast] = useState<BudgetForecast | null>(null)
  const [forecastLoading, setForecastLoading] = useState(true)

  useEffect(() => { loadData() }, [programId])

  const loadData = async () => {
    setForecastLoading(true)
    try {
      const [summary, projectsData, forecastData] = await Promise.all([
        programBudgetService.getSummary(programId).catch(() => null),
        projectsService.list({ program_id: programId }),
        programBudgetForecastService.getForecast(programId).catch(() => null),
      ])
      setBudgetSummary(summary)
      setProjects(projectsData)
      setForecast(forecastData)
    } catch { /* ignore */ } finally { setForecastLoading(false) }
  }

  const totalBudget = Number(budgetSummary?.total_budget) || projects.reduce((s, p) => s + parseFloat(p.budget || '0'), 0)
  const spentBudget = Number(budgetSummary?.spent_budget) || projects.reduce((s, p) => s + parseFloat(p.actual_cost || '0'), 0)
  const remaining = totalBudget - spentBudget
  const utilization = totalBudget > 0 ? (spentBudget / totalBudget) * 100 : 0

  return (
    <div className="p-6 space-y-6">
      <HeroSection title="Finance" description="Budget utilization, forecasting, and benefits" variant="level3"
        metrics={[
          { label: 'Total Budget', value: formatCurrency(totalBudget), icon: DollarSign, iconColor: 'bg-blue-50' },
          { label: 'Spent', value: formatCurrency(spentBudget), icon: Activity, iconColor: 'bg-orange-50' },
          { label: 'Remaining', value: formatCurrency(remaining), icon: CheckCircle2, iconColor: 'bg-green-50' },
          { label: 'Utilization', value: `${(isNaN(utilization) ? 0 : utilization).toFixed(0)}%`, icon: BarChart3, iconColor: 'bg-purple-50' },
        ]}
      />

      <BudgetCategories programId={programId} programBudget={totalBudget} canEdit={canEditBudget} />

      {/* Budget by project */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg text-neutral-950 mb-4">Budget by Project</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 px-3 text-neutral-950 font-medium">Project</th>
                <th className="py-2 px-3 text-neutral-950 font-medium">Allocated</th>
                <th className="py-2 px-3 text-neutral-950 font-medium">Spent</th>
                <th className="py-2 px-3 text-neutral-950 font-medium">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const allocated = parseFloat(p.budget || '0')
                const spent = parseFloat(p.actual_cost || '0')
                return (
                  <tr key={p.id} className="border-b border-gray-50">
                    <td className="py-3 px-3 text-neutral-950">{p.name}</td>
                    <td className="py-3 px-3 text-gray-600">{formatCurrency(allocated)}</td>
                    <td className="py-3 px-3 text-gray-600">{formatCurrency(spent)}</td>
                    <td className="py-3 px-3 text-gray-600">{formatCurrency(allocated - spent)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <BudgetForecastChart forecast={forecast} loading={forecastLoading} />
      <BenefitsTracker programId={programId} />
    </div>
  )
}

